/**
 * LessonEditModal — Модалка редактирования конкретного занятия
 *
 * Позволяет изменить: статус, тип, назначить замену педагога,
 * перенести на другую дату/время.
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, Stack, Group, Text, Select, Checkbox, Textarea,
  Button, Badge, Divider
} from '@mantine/core';
import { DatePickerInput, TimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconClock, IconCalendar } from '@tabler/icons-react';
import { updateLesson } from '../../../services/api/lessonsService';
import tokens from '../../../theme/designTokens';

const STATUS_OPTIONS = [
  { value: 'active', label: 'В силе' },
  { value: 'cancelled', label: 'Отмена' },
  { value: 'substitute', label: 'Замена' },
  { value: 'rescheduled', label: 'Перенос' },
];

const TYPE_OPTIONS = [
  { value: 'lesson', label: 'ЗН (занятие)' },
  { value: 'master_class', label: 'МК (мастер-класс)' },
  { value: 'offline', label: 'Оффлайн' },
  { value: 'online', label: 'Онлайн' },
  { value: 'group_online', label: 'Группа онлайн' },
];

const STATUS_COLORS = {
  active: 'green',
  cancelled: 'red',
  substitute: 'orange',
  rescheduled: 'blue',
  was_rescheduled: 'cyan',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

const LessonEditModal = ({ opened, onClose, lesson, organizationId, onSaved }) => {
  const [status, setStatus] = useState(lesson?.status || 'active');
  const [lessonType, setLessonType] = useState(lesson?.lesson_type || 'lesson');
  const [substituteTeacherId, setSubstituteTeacherId] = useState(
    lesson?.substitute_teacher_id ? String(lesson.substitute_teacher_id) : null
  );
  const [substituteSearching, setSubstituteSearching] = useState(
    lesson?.substitute_searching || false
  );
  const [rescheduledToDate, setRescheduledToDate] = useState(
    lesson?.rescheduled_to_date ? new Date(lesson.rescheduled_to_date + 'T00:00:00') : null
  );
  const [rescheduledToTime, setRescheduledToTime] = useState(
    lesson?.rescheduled_to_time ? lesson.rescheduled_to_time.slice(0, 5) : ''
  );
  const [notes, setNotes] = useState(lesson?.notes || '');
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Загрузить педагогов
  useEffect(() => {
    fetch('/api/users?role=TEACHER', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        const items = data?.items || data || [];
        setTeachers(
          items.map((t) => ({
            value: String(t.id),
            label: t.full_name || t.email,
          }))
        );
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {};

      if (status !== lesson.status) payload.status = status;
      if (lessonType !== lesson.lesson_type) payload.lesson_type = lessonType;

      if (status === 'substitute') {
        if (substituteTeacherId) {
          payload.substitute_teacher_id = parseInt(substituteTeacherId);
          payload.substitute_searching = false;
        } else {
          payload.substitute_searching = substituteSearching;
        }
      }

      if (status === 'rescheduled') {
        if (rescheduledToDate) {
          payload.rescheduled_to_date = rescheduledToDate.toISOString().split('T')[0];
        }
        if (rescheduledToTime) {
          payload.rescheduled_to_time = rescheduledToTime + ':00';
        }
      }

      if (notes !== (lesson.notes || '')) payload.notes = notes;

      // Если ничего не изменилось
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      await updateLesson(organizationId, lesson.id, payload);

      notifications.show({
        title: 'Сохранено',
        message: 'Занятие обновлено',
        color: 'green',
      });

      onSaved();
    } catch (err) {
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Редактирование занятия"
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Информация о занятии (read-only) */}
        <Group gap="lg">
          <div>
            <Text size="xs" c="dimmed">Группа</Text>
            <Text fw={600}>{lesson?.group_name}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Дата</Text>
            <Text fw={500}>{formatDate(lesson?.scheduled_date)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Время</Text>
            <Text fw={500}>
              {formatTime(lesson?.start_time)}–{formatTime(lesson?.end_time)}
            </Text>
          </div>
        </Group>

        <Group gap="sm">
          {lesson?.subject_name && (
            <Badge size="sm" variant="filled" color="blue" radius="xl">
              {lesson.subject_name}
            </Badge>
          )}
          <Badge size="sm" variant="filled" color={STATUS_COLORS[lesson?.status] || 'gray'} radius="xl">
            {STATUS_OPTIONS.find((o) => o.value === lesson?.status)?.label || lesson?.status}
          </Badge>
        </Group>

        <Divider />

        {/* Тип занятия */}
        <Select
          label="Тип занятия"
          data={TYPE_OPTIONS}
          value={lessonType}
          onChange={setLessonType}
          radius="xl"
        />

        {/* Статус */}
        <Select
          label="Статус"
          data={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          radius="xl"
        />

        {/* Замена педагога (при статусе "Замена") */}
        {status === 'substitute' && (
          <>
            <Select
              label="Замена педагога"
              placeholder="Выберите педагога"
              data={teachers}
              value={substituteTeacherId}
              onChange={(val) => {
                setSubstituteTeacherId(val);
                if (val) setSubstituteSearching(false);
              }}
              searchable
              clearable
              radius="xl"
            />
            {!substituteTeacherId && (
              <Checkbox
                label="В поиске педагога"
                checked={substituteSearching}
                onChange={(e) => setSubstituteSearching(e.currentTarget.checked)}
                radius="xl"
              />
            )}
          </>
        )}

        {/* Перенос (при статусе "Перенос") */}
        {status === 'rescheduled' && (
          <Group grow>
            <DatePickerInput
              label="Дата переноса"
              placeholder="Выберите дату"
              value={rescheduledToDate}
              onChange={setRescheduledToDate}
              leftSection={<IconCalendar size={16} />}
              radius="xl"
              clearable
            />
            <TimePicker
              label="Время переноса"
              value={rescheduledToTime}
              onChange={setRescheduledToTime}
              withDropdown
              radius="xl"
            />
          </Group>
        )}

        {/* Заметки */}
        <Textarea
          label="Заметки"
          placeholder="Дополнительная информация"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={2}
          radius="xl"
        />

        {/* Кнопки */}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} radius="xl">
            Отмена
          </Button>
          <Button onClick={handleSave} loading={saving} radius="xl">
            Сохранить
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default LessonEditModal;
